import React from 'react';
import ReactMarkdown from 'react-markdown';

import { Box, Button, Typography } from '@mui/material';

import { CommonComponentProps } from '../pages/types';

interface TextBoxProps extends CommonComponentProps {
  texts?: {
    type:
      | 'title'
      | 'h2'
      | 'h3'
      | 'h4'
      | 'subtitle'
      | 'paragraph'
      | 'button'
      | 'primaryButton'
      | 'textButton';
    contents: string;
    link?: string;
  }[];
  textBoxGap?: string;
}

const TextBox: React.FC<TextBoxProps> = ({
  id,
  texts = [],
  textBoxGap = '1rem',
  sx,
}) => {
  const handleButtonClick = (link?: string) => {
    if (!link) return;

    // Check if it's an internal scroll link (starts with # and targets an element ID)
    if (link.startsWith('*')) {
      // This is a scroll-to-element link
      const elementId = link.substring(1);
      const element = document.getElementById(elementId);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      } else {
        console.warn(`Element with ID "${elementId}" not found`);
      }
    } else {
      // External link - open in same window
      window.location.href = link;
    }
  };

  return (
    <Box
      id={id}
      sx={{
        maxWidth: '1100px',
        width: '100%',
        margin: '0 auto',
        gap: textBoxGap,
        display: 'flex',
        flexDirection: 'column',
        ...sx,
      }}
    >
      {texts?.map((text, index) => {
        switch (text.type) {
          case 'title':
            return (
              <Typography variant="h2" key={index}>
                <ReactMarkdown>{text.contents}</ReactMarkdown>
              </Typography>
            );
          case 'h2':
            return (
              <Typography variant="h2" component="h2" key={index}>
                <ReactMarkdown
                  components={{
                    p: ({ children }) => <>{children}</>,
                  }}
                >
                  {text.contents}
                </ReactMarkdown>
              </Typography>
            );
          case 'h3':
            return (
              <Typography variant="h3" key={index}>
                <ReactMarkdown
                  components={{
                    p: ({ children }) => <>{children}</>,
                  }}
                >
                  {text.contents}
                </ReactMarkdown>
              </Typography>
            );
          case 'h4':
            return (
              <Typography variant="h4" key={index}>
                <ReactMarkdown
                  components={{
                    p: ({ children }) => <>{children}</>,
                  }}
                >
                  {text.contents}
                </ReactMarkdown>
              </Typography>
            );
          case 'subtitle':
            return (
              <Typography variant="h4" key={index}>
                <ReactMarkdown
                  components={{
                    p: ({ children }) => <>{children}</>,
                  }}
                >
                  {text.contents}
                </ReactMarkdown>
              </Typography>
            );
          case 'paragraph':
            return (
              <Typography variant="body1" key={index}>
                <ReactMarkdown
                  components={{
                    p: ({ children }) => <>{children}</>,
                  }}
                >
                  {text.contents}
                </ReactMarkdown>
              </Typography>
            );
          case 'primaryButton':
            return (
              <Button
                color="primary"
                variant="contained"
                key={index}
                onClick={() => handleButtonClick(text.link)}
                sx={{ width: 'fit-content' }}
              >
                <ReactMarkdown
                  components={{
                    p: ({ children }) => <>{children}</>,
                  }}
                >
                  {text.contents}
                </ReactMarkdown>
              </Button>
            );
          case 'button':
            return (
              <Button
                color="secondary"
                variant="contained"
                key={index}
                onClick={() => handleButtonClick(text.link)}
                sx={{ width: 'fit-content' }}
              >
                <ReactMarkdown
                  components={{
                    p: ({ children }) => <>{children}</>,
                  }}
                >
                  {text.contents}
                </ReactMarkdown>
              </Button>
            );
          case 'textButton':
            return (
              <Button
                variant="text"
                key={index}
                onClick={() => handleButtonClick(text.link)}
                sx={{
                  width: 'fit-content',
                }}
              >
                <ReactMarkdown
                  components={{
                    p: ({ children }) => <>{children}</>,
                  }}
                >
                  {text.contents}
                </ReactMarkdown>
              </Button>
            );
          default:
            return null;
        }
      })}
    </Box>
  );
};

export default TextBox;
